import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styles from './styles.module.css';
import { setBranchId } from '../../../../auth/actions';
import { Button, Dropdown, Input, InputWithSelect, SearchableSelect } from '../../../../../common/ui';
import { showAlert } from '../../../../../common/actions';
import { searchStockById, resetSearchedStock, updateStock, searchProductsForAddStock, searchSuppliers } from '../../actions';
import Axios from '../../../../../common/util/Axios';
import ReturnChips from '../../../supplierReturn/components/ReturnChips';
import { ReplacementChip } from '../../../supplierReturn/components/ReplacementLink';
import { fetchPostedReturnsForSource } from '../../../supplierReturn/actions';

/** Same as Add New Stock: code – name – variantName (see search-for-add-stock API). */
function getProductSearchOptionLabel(o) {
    if (!o || typeof o !== 'object') return '—';
    const mainName =
        o.baseName != null && String(o.baseName).trim() !== '' ? o.baseName : o.name;
    const parts = [o.code, mainName, o.variantName].filter(
        (x) => x != null && String(x).trim() !== ''
    );
    return parts.join(' – ') || '—';
}

const UpdateStock = memo(
    ({
        stock: { searchedStockById, suggestProducts, suggestSuppliers, isSuggestedProductsLoading, isSuggestedSuppliersLoading },
        showAlert,
        auth: { companyId, branchId, userId, user },
        searchStockById,
        resetSearchedStock,
        updateStock,
        searchProductsForAddStock,
        searchSuppliers,
        fetchPostedReturnsForSource,
    }) => {
        const { id } = useParams();
        const [searchParams] = useSearchParams();
        const statusParam = String(searchParams.get('status') || 'update').toLowerCase();
        const isApprove = statusParam === 'approve';

        const buildRedirectUrl = () => {
            const redirectPage = searchParams.get('redirectPage');
            const redirectPageSize = searchParams.get('redirectPageSize');
            const redirectFocusIndex = searchParams.get('redirectFocusIndex');
            if (redirectPage || redirectPageSize || redirectFocusIndex) {
                const queryParams = new URLSearchParams();
                if (redirectPage) queryParams.set('redirectPage', redirectPage);
                if (redirectPageSize) queryParams.set('redirectPageSize', redirectPageSize);
                if (redirectFocusIndex) queryParams.set('redirectFocusIndex', redirectFocusIndex);
                return `/inventory/stocks?${queryParams.toString()}`;
            }
            return '/inventory/stocks';
        };

        const qtyUnitOptions = useMemo(
            () => [
                { value: 'pcs', label: 'pcs' },
                { value: 'kg', label: 'kg' },
                { value: 'g', label: 'g' },
                { value: 'l', label: 'l' },
                { value: 'ml', label: 'ml' },
                { value: 'm', label: 'm' },
                { value: 'cm', label: 'cm' },
            ],
            []
        );

        const discountTypeOptions = useMemo(
            () => [
                { value: 'TOTAL_AMOUNT', label: 'Total Amount' },
                { value: 'PER_UNIT', label: 'Per Unit' },
            ],
            []
        );

        const discountUnitOptions = useMemo(
            () => [
                { value: 'Rs', label: 'Rs' },
                { value: '%', label: '%' },
            ],
            []
        );

        const [warrantyTypeOptions, setWarrantyTypeOptions] = useState([]);
        const [isUnitCostDisabled, setIsUnitCostDisabled] = useState(false);
        const [isCostOfGoodsDisabled, setIsCostOfGoodsDisabled] = useState(false);
        const [originalInitialQty, setOriginalInitialQty] = useState('');
        const [originalSerialNumberList, setOriginalSerialNumberList] = useState([]);
        const [originalWarrantyList, setOriginalWarrantyList] = useState([]);
        const [promotionId, setPromotionId] = useState(null);

        const [formData, setFormData] = useState({
            productId: null,
            productLabel: '',
            supplierId: null,
            supplierLabel: '',

            unitCost: '',
            qty: '',
            unit: 'pcs',
            discountType: '',
            discountValue: '',
            discountUnit: 'Rs',
            costOfGoods: '',

            sellingPrice: '',
            sellingDiscountValue: '',
            sellingDiscountUnit: 'Rs',
            loyaltyDiscountValue: '',
            loyaltyDiscountUnit: 'Rs',
            wholesaleDiscountValue: '',
            wholesaleDiscountUnit: 'Rs',

            expireDate: '',

            serialNumberList: [],
            warrantyList: [],
        });

        const [serialNumber, setSerialNumber] = useState('');
        const [serialRangeMin, setSerialRangeMin] = useState('');
        const [serialRangeMax, setSerialRangeMax] = useState('');
        const [warrantyTypeId, setWarrantyTypeId] = useState('');
        const [warrantyDescription, setWarrantyDescription] = useState('');
        const [displayWarrantyPeriod, setDisplayWarrantyPeriod] = useState('');

        const [isUpdating, setIsUpdating] = useState(false);
        const [returnChips, setReturnChips] = useState([]);
        const navigate = useNavigate();

        const isSerialRangeDisabled = formData.serialNumberList.length > 0;
        const isSingleSerialDisabled = Boolean(serialRangeMin || serialRangeMax);

        useEffect(() => {
            if (id) {
                resetSearchedStock();
                const setInitialProduct = (product) => {
                    if (product && product.id) {
                        setFormData((prev) => ({ ...prev, productId: product.id, productLabel: product.name ?? '' }));
                    }
                };
                searchStockById(id, setInitialProduct);
            }
        }, [id, resetSearchedStock, searchStockById]);

        useEffect(() => {
            searchSuppliers({});
        }, [searchSuppliers]);

        useEffect(() => {
            if (!companyId) return;
            Axios.get('/api/dashboard/settings/find-warranty-periods', { params: { companyId } })
                .then((res) => {
                    const data = res.data?.data ?? res.data;
                    setWarrantyTypeOptions(Array.isArray(data) ? data : []);
                })
                .catch(() => setWarrantyTypeOptions([]));
        }, [companyId]);

        useEffect(() => {
            let cancelled = false;
            setReturnChips([]);
            async function loadChips() {
                if (!id) {
                    return;
                }
                const rows = await fetchPostedReturnsForSource('stock', id);
                if (!cancelled) setReturnChips(Array.isArray(rows) ? rows : []);
            }
            loadChips();
            return () => {
                cancelled = true;
            };
        }, [id, branchId, fetchPostedReturnsForSource]);

        useEffect(() => {
            const s = searchedStockById;
            if (!s || typeof s !== 'object' || Array.isArray(s)) return;
            const expireAt = s.expire_at ?? s.expireDate;
            let expireDateStr = '';
            if (expireAt) {
                const d = typeof expireAt === 'number' ? new Date(expireAt * 1000) : new Date(expireAt);
                if (!Number.isNaN(d.getTime())) {
                    expireDateStr = d.toISOString().slice(0, 10);
                }
            }
            const supplierId = s.supplier_id ?? s.supplierId ?? '';
            const supplier = suggestSuppliers?.find((sup) => String(sup.id) === String(supplierId));
            const supplierLabel = supplier ? (supplier.name || supplier.companyname || '') : '';
            setOriginalInitialQty(s.initial_qty != null ? String(s.initial_qty) : s.initialQty != null ? String(s.initialQty) : '');
            const serialsFromApi = Array.isArray(s.serialNumbers) ? s.serialNumbers : [];
            const initialSerials = serialsFromApi.map((sn) =>
                typeof sn === 'object' && sn !== null && (sn.id || sn.serialNumber)
                    ? { id: sn.id, serialNumber: sn.serialNumber || String(sn) }
                    : { serialNumber: String(sn) }
            );
            const initialWarranties = Array.isArray(s.warranties)
                ? s.warranties.map((w) => ({
                    id: w.id,
                    warrantyId: w.warrantyId,
                    description: w.description ?? '',
                }))
                : [];

            setOriginalSerialNumberList(serialsFromApi.map((sn) => ({ id: sn.id, serialNumber: sn.serialNumber || String(sn) })));
            setOriginalWarrantyList(initialWarranties);
            setPromotionId(s.promotionId ?? s.promotion_id ?? null);

            setFormData((prev) => ({
                ...prev,
                productId: s.product_id ?? s.productid ?? prev.productId,
                productLabel: s.productname ?? prev.productLabel,
                supplierId: supplierId || prev.supplierId,
                supplierLabel: supplierLabel || prev.supplierLabel,
                unit: s.unit || prev.unit,
                qty: s.initial_qty != null ? String(s.initial_qty) : s.initialQty != null ? String(s.initialQty) : prev.qty,
                unitCost: s.unit_cost != null ? String(s.unit_cost) : s.unitCost != null ? String(s.unitCost) : prev.unitCost,
                costOfGoods: s.cost_of_goods != null ? String(s.cost_of_goods) : s.costOfGoods != null ? String(s.costOfGoods) : prev.costOfGoods,
                sellingPrice: s.selling_price != null ? String(s.selling_price) : s.unitPrice != null ? String(s.unitPrice) : prev.sellingPrice,
                sellingDiscountValue: s.discount != null ? String(s.discount) : prev.sellingDiscountValue,
                sellingDiscountUnit: s.discount_unit ?? s.discountUnit ?? prev.sellingDiscountUnit,
                loyaltyDiscountValue: s.loyaltyDiscount != null ? String(s.loyaltyDiscount) : prev.loyaltyDiscountValue,
                loyaltyDiscountUnit: s.loyaltyDiscountUnit ?? prev.loyaltyDiscountUnit,
                wholesaleDiscountValue: s.wholesale_discount != null ? String(s.wholesale_discount) : s.wholesaleDiscount != null ? String(s.wholesaleDiscount) : prev.wholesaleDiscountValue,
                expireDate: expireDateStr || prev.expireDate,
                serialNumberList: initialSerials.length > 0 ? initialSerials : prev.serialNumberList,
                warrantyList: initialWarranties.length > 0 ? initialWarranties : prev.warrantyList,
            }));
        }, [searchedStockById, suggestSuppliers]);

        const uiProductOptions = useMemo(() => {
            const base = (suggestProducts || []).map((p) => ({
                id: p.id,
                label: p.label ?? getProductSearchOptionLabel(p),
                ...p,
            }));

            if (formData.productId && formData.productLabel) {
                const exists = base.some((p) => String(p.id) === String(formData.productId));
                if (!exists) {
                    base.unshift({
                        id: formData.productId,
                        label: formData.productLabel,
                    });
                }
            }

            return base;
        }, [suggestProducts, formData.productId, formData.productLabel]);

        const uiSupplierOptions = useMemo(
            () =>
                (suggestSuppliers || []).map((s) => ({
                    id: s.id,
                    label: s.label ?? s.name ?? s.companyname ?? String(s.id),
                    ...s,
                })),
            [suggestSuppliers]
        );

        const uiWarrantyTypeOptions = useMemo(
            () =>
                warrantyTypeOptions.map((w) => ({
                    value: w.id,
                    label: w.name ?? String(w.id),
                    ...w,
                })),
            [warrantyTypeOptions]
        );

        const onProductSearch = (term) => {
            searchProductsForAddStock(term || '');
        };

        const onchangeBuyingValue = useCallback(
            ({
                unitCost = formData.unitCost || 0,
                quantity = formData.qty || 0,
                discountType = formData.discountType,
                discountValue = formData.discountValue || 0,
                discountUnit = formData.discountUnit,
                costOfGoods = formData.costOfGoods || 0,
                isCostOfGoodsEntered = isUnitCostDisabled,
                isUnitPriceEntered = isCostOfGoodsDisabled,
            }) => {
                const u = parseFloat(unitCost) || 0;
                const q = parseFloat(quantity) || 0;
                const dVal = parseFloat(discountValue) || 0;
                const c = parseFloat(costOfGoods) || 0;
                let totalDiscountAmount = 0;
                if (discountType === 'TOTAL_AMOUNT' && discountUnit === 'Rs') {
                    totalDiscountAmount = dVal;
                } else if (discountType === 'PER_UNIT' && discountUnit === 'Rs') {
                    totalDiscountAmount = dVal * q;
                } else if (discountUnit === '%') {
                    if (isCostOfGoodsEntered) {
                        totalDiscountAmount = (c / (100 - dVal)) * dVal;
                        totalDiscountAmount = Math.round(totalDiscountAmount * 100) / 100;
                    } else if (isUnitPriceEntered) {
                        totalDiscountAmount = (u * q * dVal) / 100;
                        totalDiscountAmount = Math.round(totalDiscountAmount * 100) / 100;
                    }
                }
                let calculatedUnitPrice;
                let calculatedTotalCost;
                if (isCostOfGoodsEntered && q > 0) {
                    calculatedUnitPrice = ((c + totalDiscountAmount) / q).toFixed(2);
                } else if (isUnitPriceEntered) {
                    calculatedTotalCost = (u * q - totalDiscountAmount).toFixed(2);
                }
                return {
                    calculatedUnitPrice: calculatedUnitPrice ?? formData.unitCost,
                    calculatedTotalCost: calculatedTotalCost ?? formData.costOfGoods,
                };
            },
            [formData.unitCost, formData.qty, formData.discountType, formData.discountValue, formData.discountUnit, formData.costOfGoods, isUnitCostDisabled, isCostOfGoodsDisabled]
        );

        const handleUnitCostChange = (val) => {
            if (val === '') {
                setIsCostOfGoodsDisabled(false);
                setFormData((prev) => ({ ...prev, unitCost: '', costOfGoods: '' }));
                return;
            }
            if (!/^[0-9]*\.?[0-9]*$/.test(val)) return;
            setFormData((prev) => ({ ...prev, unitCost: val }));
            const num = parseFloat(val);
            if (!Number.isNaN(num)) {
                setIsCostOfGoodsDisabled(true);
                const { calculatedTotalCost } = onchangeBuyingValue({
                    unitCost: num,
                    isCostOfGoodsEntered: false,
                    isUnitPriceEntered: true,
                });
                setFormData((prev) => ({ ...prev, costOfGoods: calculatedTotalCost }));
            }
        };

        const handleCostOfGoodsChange = (val) => {
            if (val === '') {
                setIsUnitCostDisabled(false);
                setFormData((prev) => ({ ...prev, unitCost: '', costOfGoods: '' }));
                return;
            }
            if (!/^[0-9]*\.?[0-9]*$/.test(val)) return;
            setFormData((prev) => ({ ...prev, costOfGoods: val }));
            const num = parseFloat(val);
            if (!Number.isNaN(num)) {
                setIsUnitCostDisabled(true);
                const { calculatedUnitPrice } = onchangeBuyingValue({
                    costOfGoods: num,
                    isCostOfGoodsEntered: true,
                    isUnitPriceEntered: false,
                });
                setFormData((prev) => ({ ...prev, unitCost: calculatedUnitPrice }));
            }
        };

        const handleQtyChange = (val) => {
            setFormData((prev) => ({ ...prev, qty: val }));
            if (val === '') return;
            const num = parseFloat(val);
            if (!Number.isNaN(num)) {
                const { calculatedUnitPrice, calculatedTotalCost } = onchangeBuyingValue({ quantity: num });
                setFormData((prev) => ({ ...prev, unitCost: calculatedUnitPrice, costOfGoods: calculatedTotalCost }));
            }
        };

        const handleDiscountValueChange = (val) => {
            setFormData((prev) => ({ ...prev, discountValue: val }));
            if (val === '') return;
            const num = parseFloat(val);
            if (!Number.isNaN(num)) {
                const { calculatedUnitPrice, calculatedTotalCost } = onchangeBuyingValue({ discountValue: num });
                setFormData((prev) => ({ ...prev, unitCost: calculatedUnitPrice, costOfGoods: calculatedTotalCost }));
            }
        };

        const handleDiscountTypeChange = (val) => {
            setFormData((prev) => ({ ...prev, discountType: val }));
            const { calculatedUnitPrice, calculatedTotalCost } = onchangeBuyingValue({ discountType: val });
            setFormData((prev) => ({ ...prev, unitCost: calculatedUnitPrice, costOfGoods: calculatedTotalCost }));
        };

        const handleDiscountUnitChange = (val) => {
            setFormData((prev) => ({ ...prev, discountUnit: val }));
            const { calculatedUnitPrice, calculatedTotalCost } = onchangeBuyingValue({ discountUnit: val });
            setFormData((prev) => ({ ...prev, unitCost: calculatedUnitPrice, costOfGoods: calculatedTotalCost }));
        };

        const getSerialValue = (item) => (typeof item === 'object' && item !== null && item.serialNumber != null ? item.serialNumber : String(item));

        const addSerialNumber = () => {
            const s = String(serialNumber || '').trim();
            if (!s) return;
            setFormData((prev) => {
                const exists = prev.serialNumberList.some((item) => getSerialValue(item) === s);
                return exists ? prev : { ...prev, serialNumberList: [...prev.serialNumberList, s] };
            });
            setSerialNumber('');
        };

        const removeSerialAt = (index) => {
            setFormData((prev) => ({
                ...prev,
                serialNumberList: prev.serialNumberList.filter((_, i) => i !== index),
            }));
        };

        const clearAllSerials = () => {
            setFormData((prev) => ({ ...prev, serialNumberList: [] }));
        };

        const addWarranty = () => {
            if (!warrantyTypeId) return;
            setFormData((prev) => ({
                ...prev,
                warrantyList: [
                    ...prev.warrantyList,
                    { warrantyId: warrantyTypeId, description: warrantyDescription },
                ],
            }));
            setWarrantyTypeId('');
            setWarrantyDescription('');
            setDisplayWarrantyPeriod('');
        };

        const removeWarrantyAt = (index) => {
            setFormData((prev) => ({
                ...prev,
                warrantyList: prev.warrantyList.filter((_, i) => i !== index),
            }));
        };

        const clearAllWarranties = () => {
            setFormData((prev) => ({ ...prev, warrantyList: [] }));
        };

        const calculateDiscountAmount = (price, value, unit) => {
            if (!value) return 0;
            return unit === 'Rs' ? Number(value) : (Number(value) * Number(price)) / 100;
        };

        const runValidations = () => {
            if (!formData.productId) {
                showAlert({ isAlertOpen: true, alertType: 'danger', alertMessage: 'Please select a product.' });
                return false;
            }
            if (!formData.supplierId) {
                showAlert({ isAlertOpen: true, alertType: 'danger', alertMessage: 'Please select a supplier.' });
                return false;
            }
            if (!formData.unitCost) {
                showAlert({ isAlertOpen: true, alertType: 'danger', alertMessage: 'Please enter unit cost.' });
                return false;
            }
            if (!formData.qty) {
                showAlert({ isAlertOpen: true, alertType: 'danger', alertMessage: 'Please enter quantity.' });
                return false;
            }
            if (!formData.unit) {
                showAlert({ isAlertOpen: true, alertType: 'danger', alertMessage: 'Please select unit.' });
                return false;
            }
            if (originalInitialQty && parseFloat(formData.qty) < parseFloat(originalInitialQty)) {
                showAlert({ isAlertOpen: true, alertType: 'danger', alertMessage: 'Quantity cannot be less than original quantity.' });
                return false;
            }
            if (formData.discountValue && !formData.discountType) {
                showAlert({ isAlertOpen: true, alertType: 'danger', alertMessage: 'Please select discount type when discount value is entered.' });
                return false;
            }
            if (!formData.costOfGoods) {
                showAlert({ isAlertOpen: true, alertType: 'danger', alertMessage: 'Please enter cost of goods.' });
                return false;
            }
            if (!formData.sellingPrice) {
                showAlert({ isAlertOpen: true, alertType: 'danger', alertMessage: 'Please enter selling price.' });
                return false;
            }
            return true;
        };

        const onSubmit = async () => {
            if (isUpdating) return;
            if (!runValidations()) return;
            setIsUpdating(true);
            try {
                const sellingPriceNum = formData.sellingPrice ? Number(formData.sellingPrice) : 0;
                const expireAt = formData.expireDate ? Math.floor(new Date(formData.expireDate).getTime() / 1000) : null;
                const updatedStockBody = {
                    product_id: formData.productId,
                    initial_qty: formData.qty ? Number(formData.qty) : null,
                    unit: formData.unit || 'pcs',
                    cost_of_goods: formData.costOfGoods ? Number(formData.costOfGoods) : null,
                    unit_cost: formData.unitCost ? Number(formData.unitCost) : null,
                    selling_price: sellingPriceNum,
                    discount: formData.sellingDiscountValue ? Number(formData.sellingDiscountValue) : null,
                    discount_unit: formData.sellingDiscountValue ? (formData.sellingDiscountUnit || 'Rs') : null,
                    supplier_id: formData.supplierId || null,
                    wholesale_discount: calculateDiscountAmount(sellingPriceNum, formData.wholesaleDiscountValue, formData.wholesaleDiscountUnit) || 0,
                    expire_at: expireAt,
                    isTemporary: 0,
                };

                const requestBody = { updatedStockBody };

                const newQty = formData.qty ? Number(formData.qty) : 0;
                const origQty = originalInitialQty ? Number(originalInitialQty) : 0;
                if (newQty !== origQty) {
                    const loggedUserId = userId ?? user?.id ?? null;
                    requestBody.stockLedgerBody = {
                        stockId: id,
                        quantity: newQty - origQty,
                        type: 'STOCK_ADJUSTMENT',
                        referenceId: null,
                        description: 'Update initial stock quantity',
                        userId: loggedUserId,
                    };
                }

                const hasLoyaltyDiscount = formData.loyaltyDiscountValue && formData.loyaltyDiscountUnit;
                if (hasLoyaltyDiscount) {
                    const createdAt = new Date().toISOString();
                    if (promotionId) {
                        requestBody.promotionExist = true;
                        requestBody.promotion = {
                            id: promotionId,
                            value: formData.loyaltyDiscountValue,
                            unit: formData.loyaltyDiscountUnit,
                        };
                    } else {
                        requestBody.promotionExist = false;
                        requestBody.promotion = {
                            productid: formData.productId,
                            value: formData.loyaltyDiscountValue,
                            unit: formData.loyaltyDiscountUnit,
                            productname: formData.productLabel || null,
                            expiredat: null,
                            is_loyalty_promo: 1,
                            stock_id: id,
                            promotion_type: 'PRODUCT_DISCOUNT',
                            rule: null,
                            rule_variables: null,
                            companyId,
                            branchId,
                            createdat: createdAt,
                            startfrom: createdAt,
                        };
                    }
                }

                const currentSerialValues = formData.serialNumberList.map(getSerialValue);
                const originalSerialValues = originalSerialNumberList.map((o) => o.serialNumber);
                const deletedSerialNumbers = originalSerialNumberList.filter(
                    (o) => !currentSerialValues.includes(o.serialNumber)
                );
                const newlyAddedSerialNumbers = currentSerialValues.filter(
                    (sn) => !originalSerialValues.includes(sn)
                );
                if (deletedSerialNumbers.length > 0 || newlyAddedSerialNumbers.length > 0) {
                    requestBody.stockSerialBody = {
                        stockId: id,
                        deletedSerialNumbers,
                        newlyAddedSerialNumbers,
                    };
                }

                const deletedWarranties = originalWarrantyList.filter(
                    (o) => !formData.warrantyList.some((c) => c.id != null && c.id === o.id)
                );
                // Newly added = no id, or id not in original list (items without id are always new)
                const newlyAddedWarranties = formData.warrantyList
                    .filter(
                        (c) =>
                            c.id == null ||
                            !originalWarrantyList.some((o) => o.id != null && o.id === c.id)
                    )
                    .map((w) => ({
                        warrantyId: w.warrantyId != null ? String(w.warrantyId) : null,
                        description: w.description != null ? String(w.description) : null,
                    }));
                if (deletedWarranties.length > 0 || newlyAddedWarranties.length > 0) {
                    requestBody.stockWarrantyBody = {
                        stockId: id,
                        deletedWarranties,
                        newlyAddedWarranties,
                    };
                }

                const isUpdated = await updateStock(id, requestBody);
                if (isUpdated) {
                    showAlert({
                        isAlertOpen: true,
                        alertType: 'success',
                        alertMessage: `Stock ${isApprove ? 'approved' : 'updated'} successfully`,
                    });
                    navigate(buildRedirectUrl());
                } else {
                    showAlert({
                        isAlertOpen: true,
                        alertType: 'danger',
                        alertMessage: 'Operation failed. Please try again later.',
                    });
                }
            } finally {
                setIsUpdating(false);
            }
        };

        const onClickCancel = () => {
            navigate(buildRedirectUrl());
        };

        return (
            <div className={styles.mainContainer}>
                <nav className={styles.breadcrumb}>
                    <Link to="/" className={styles.breadcrumbLink}>Cashier</Link>
                    <span className={styles.breadcrumbSeparator}>/</span>
                    <Link to="/inventory/stocks" className={styles.breadcrumbLink}>Stocks</Link>
                    <span className={styles.breadcrumbSeparator}>/</span>
                    <span className={styles.breadcrumbCurrent}>{isApprove ? 'Approve Stock' : 'Update Stock'}</span>
                </nav>
                <div className={styles.topContainer}>
                    <div className={styles.header}>{isApprove ? 'Approve Stock' : 'Update Stock'}</div>
                    <div className={styles.caption}>{isApprove ? 'Approve pending stock' : 'Update stock in your store'}</div>
                </div>
                <div className={styles.bottomContainer}>
                    <div className={styles.formContainer}>
                        <div className={styles.headerContainer}>
                            <div className={styles.inputWrapper}>
                                <div className={styles.textField}>
                                    <SearchableSelect
                                        id="productId"
                                        label="Select Product by Name, Code, or Barcode"
                                        async
                                        minSearchLength={1}
                                        searchDebounceMs={300}
                                        isLoading={isSuggestedProductsLoading}
                                        options={uiProductOptions}
                                        value={formData.productId}
                                        onSearch={onProductSearch}
                                        getOptionLabel={(o) => o.label}
                                        getOptionValue={(o) => o.id}
                                        onChange={(val) => setFormData((prev) => ({ ...prev, productId: val }))}
                                        onSelect={(opt) => {
                                            setFormData((prev) => ({
                                                ...prev,
                                                productId: opt.id,
                                                productLabel: opt.label,
                                            }));
                                        }}
                                        placeholder="Type to search…"
                                    />
                                </div>
                                <div className={styles.selectWrapper}>
                                    <SearchableSelect
                                        id="supplierId"
                                        label="Supplier"
                                        options={uiSupplierOptions}
                                        value={formData.supplierId}
                                        getOptionLabel={(o) => o.label}
                                        getOptionValue={(o) => o.id}
                                        onChange={(val) => setFormData((prev) => ({ ...prev, supplierId: val }))}
                                        onSelect={(opt) => {
                                            setFormData((prev) => ({
                                                ...prev,
                                                supplierId: opt.id,
                                                supplierLabel: opt.label,
                                            }));
                                        }}
                                        placeholder="Select supplier…"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className={styles.line} />

                        <section className={styles.formSection}>
                            <div className={styles.sectionTitleContainer}>
                                <div className={styles.sectionHeader}>
                                    <div className={styles.sectionTitle}>Buying Section</div>
                                    <div className={styles.sectionCaption}>Please add your buying information</div>
                                </div>
                            </div>

                            <div className={styles.buyingGrid}>
                                <Input
                                    id="unitCost"
                                    label="Unit Cost"
                                    value={formData.unitCost}
                                    onChange={handleUnitCostChange}
                                    placeholder="0.00"
                                    disabled={isUnitCostDisabled}
                                />

                                <InputWithSelect
                                    id="qty"
                                    label="Quantity"
                                    value={formData.qty}
                                    onChange={handleQtyChange}
                                    placeholder="0"
                                    selectOptions={qtyUnitOptions}
                                    selectValue={formData.unit}
                                    onSelectChange={(val) => setFormData((prev) => ({ ...prev, unit: val }))}
                                    selectPlaceholder="pcs"
                                />
                                <ReturnChips chips={returnChips} />
                                <ReplacementChip
                                    id={searchedStockById?.relatedSupplierReturnId}
                                    returnNo={searchedStockById?.replacementReturnNo}
                                />

                                <Dropdown
                                    id="discountType"
                                    label="Discount Type"
                                    value={formData.discountType}
                                    onChange={handleDiscountTypeChange}
                                    options={discountTypeOptions}
                                    placeholder="Not Selected"
                                />

                                <InputWithSelect
                                    id="discountValue"
                                    label="Discount value"
                                    value={formData.discountValue}
                                    onChange={handleDiscountValueChange}
                                    placeholder="0"
                                    selectOptions={discountUnitOptions}
                                    selectValue={formData.discountUnit}
                                    onSelectChange={handleDiscountUnitChange}
                                    selectPlaceholder="Rs"
                                />

                                <Input
                                    id="costOfGoods"
                                    label="Cost of Goods"
                                    value={formData.costOfGoods}
                                    onChange={handleCostOfGoodsChange}
                                    placeholder="0.00"
                                    className={styles.span2}
                                    disabled={isCostOfGoodsDisabled}
                                />
                            </div>
                        </section>

                        <div className={styles.sectionDivider} />

                        <section className={styles.formSection}>
                            <div className={styles.sectionTitleContainer}>
                                <div className={styles.sectionHeader}>
                                    <div className={styles.sectionTitle}>Selling Section</div>
                                    <div className={styles.sectionCaption}>Please add your selling information</div>
                                </div>
                            </div>
                            <div className={styles.bottomDiv}>
                                <Input
                                    id="sellingPrice"
                                    label="Selling Price"
                                    value={formData.sellingPrice}
                                    onChange={(val) => setFormData((prev) => ({ ...prev, sellingPrice: val }))}
                                    placeholder="0.00"
                                />

                                <InputWithSelect
                                    id="sellingDiscount"
                                    label="Selling Discount"
                                    value={formData.sellingDiscountValue}
                                    onChange={(val) => setFormData((prev) => ({ ...prev, sellingDiscountValue: val }))}
                                    placeholder="0"
                                    selectOptions={discountUnitOptions}
                                    selectValue={formData.sellingDiscountUnit}
                                    onSelectChange={(val) => setFormData((prev) => ({ ...prev, sellingDiscountUnit: val }))}
                                />

                                <InputWithSelect
                                    id="loyaltyDiscount"
                                    label="Loyalty Discount"
                                    value={formData.loyaltyDiscountValue}
                                    onChange={(val) => setFormData((prev) => ({ ...prev, loyaltyDiscountValue: val }))}
                                    placeholder="0"
                                    selectOptions={discountUnitOptions}
                                    selectValue={formData.loyaltyDiscountUnit}
                                    onSelectChange={(val) => setFormData((prev) => ({ ...prev, loyaltyDiscountUnit: val }))}
                                />

                                <InputWithSelect
                                    id="wholesaleDiscount"
                                    label="Wholesale Discount"
                                    value={formData.wholesaleDiscountValue}
                                    onChange={(val) => setFormData((prev) => ({ ...prev, wholesaleDiscountValue: val }))}
                                    placeholder="0"
                                    selectOptions={discountUnitOptions}
                                    selectValue={formData.wholesaleDiscountUnit}
                                    onSelectChange={(val) => setFormData((prev) => ({ ...prev, wholesaleDiscountUnit: val }))}
                                />

                                <Input
                                    id="expireDate"
                                    label="Expire Date"
                                    type="date"
                                    value={formData.expireDate}
                                    onChange={(val) => setFormData((prev) => ({ ...prev, expireDate: val }))}
                                />
                            </div>
                        </section>

                        <div className={styles.line} />

                        <section className={styles.formSection}>
                            <div className={styles.sectionTitleContainer}>
                                <div className={styles.sectionHeader}>
                                    <div className={styles.sectionTitle}>Serial Numbers</div>
                                    <div className={styles.sectionCaption}>Add serial numbers for this stock item</div>
                                </div>
                            </div>

                            <div className={styles.serialEntryRow}>
                                <div className={styles.serialEntryCol}>
                                    <Input
                                        id="serialNumber"
                                        label="Serial Number"
                                        value={serialNumber}
                                        onChange={setSerialNumber}
                                        placeholder="Enter serial number"
                                        disabled={isSingleSerialDisabled}
                                    />
                                    <div className={styles.inlineActions}>
                                        <Button
                                            type="button"
                                            variant="secondary"
                                            onClick={addSerialNumber}
                                            disabled={isSingleSerialDisabled}
                                        >
                                            Add
                                        </Button>
                                    </div>
                                </div>

                                <div className={styles.serialRangeBlock}>
                                    <div className={styles.serialRangeTitle}>Or serial range</div>
                                    <div className={styles.serialRangeRow}>
                                        <Input
                                            id="serialRangeMin"
                                            label="Min"
                                            value={serialRangeMin}
                                            onChange={setSerialRangeMin}
                                            placeholder="Min"
                                            disabled={isSerialRangeDisabled}
                                        />
                                        <div className={styles.serialRangeDash}>-</div>
                                        <Input
                                            id="serialRangeMax"
                                            label="Max"
                                            value={serialRangeMax}
                                            onChange={setSerialRangeMax}
                                            placeholder="Max"
                                            disabled={isSerialRangeDisabled}
                                        />
                                    </div>
                                    {isSerialRangeDisabled && (
                                        <div className={styles.helperText}>
                                            Serial range is disabled because serial numbers are already added.
                                        </div>
                                    )}
                                </div>
                            </div>

                            {isSingleSerialDisabled && (
                                <div className={styles.helperText}>
                                    Single serial input is disabled because a serial range is entered.
                                </div>
                            )}

                            <div className={styles.tagsWrap}>
                                {formData.serialNumberList.map((s, index) => (
                                    <span key={(typeof s === 'object' && s?.id) ? s.id : `sn-${index}-${getSerialValue(s)}`} className={styles.tag}>
                                        <span className={styles.tagText}>{getSerialValue(s)}</span>
                                        <button
                                            type="button"
                                            className={styles.tagRemove}
                                            onClick={() => removeSerialAt(index)}
                                            aria-label="Remove serial"
                                        >
                                            ×
                                        </button>
                                    </span>
                                ))}
                                {formData.serialNumberList.length > 0 && (
                                    <button type="button" className={styles.clearAll} onClick={clearAllSerials}>
                                        Clear all
                                    </button>
                                )}
                            </div>
                        </section>

                        <div className={styles.line} />

                        <section className={styles.formSection}>
                            <div className={styles.sectionTitleContainer}>
                                <div className={styles.sectionHeader}>
                                    <div className={styles.sectionTitle}>Warranty Information</div>
                                    <div className={styles.sectionCaption}>Attach warranty details (optional)</div>
                                </div>
                            </div>

                            <div className={styles.warrantyRow}>
                                <Dropdown
                                    id="warrantyType"
                                    label="Select Warranty"
                                    value={warrantyTypeId}
                                    onChange={(val) => {
                                        setWarrantyTypeId(val);
                                        const selected = warrantyTypeOptions.find((w) => String(w.id) === String(val));
                                        if (selected?.period && selected?.unit) {
                                            setDisplayWarrantyPeriod(`${selected.period} ${selected.unit}`);
                                        } else {
                                            setDisplayWarrantyPeriod('');
                                        }
                                    }}
                                    options={uiWarrantyTypeOptions}
                                    placeholder="Not Selected"
                                />

                                <Input
                                    id="warrantyDescription"
                                    label="Description"
                                    value={warrantyDescription}
                                    onChange={setWarrantyDescription}
                                    placeholder="Optional"
                                />

                                <div className={styles.inlineActions}>
                                    <Button type="button" variant="secondary" onClick={addWarranty}>
                                        Add
                                    </Button>
                                </div>
                            </div>

                            {displayWarrantyPeriod && (
                                <div className={styles.helperText}>Period: {displayWarrantyPeriod}</div>
                            )}

                            <div className={styles.tagsWrap}>
                                {formData.warrantyList.map((w, index) => (
                                    <span key={`${w.warrantyId}-${index}`} className={styles.tag}>
                                        <span className={styles.tagText}>
                                            {String(w.warrantyId)}{w.description ? ` — ${w.description}` : ''}
                                        </span>
                                        <button
                                            type="button"
                                            className={styles.tagRemove}
                                            onClick={() => removeWarrantyAt(index)}
                                            aria-label="Remove warranty"
                                        >
                                            ×
                                        </button>
                                    </span>
                                ))}
                                {formData.warrantyList.length > 0 && (
                                    <button type="button" className={styles.clearAll} onClick={clearAllWarranties}>
                                        Clear all
                                    </button>
                                )}
                            </div>
                        </section>

                        <div className={styles.footerDivider} />

                        <div className={styles.buttonWrapper}>
                            <Button type="button" onClick={onSubmit} disabled={isUpdating}>
                                {isUpdating ? 'Loading...' : (isApprove ? 'Approve' : 'Update')}
                            </Button>
                            <Button type="button" variant="secondary" onClick={onClickCancel}>
                                Cancel
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
);

const mapStateToProps = (state) => ({
    auth: state.authState,
    stock: state.stocksState,
});

export default connect(mapStateToProps, {
    setBranchId,
    showAlert,
    searchStockById,
    resetSearchedStock,
    updateStock,
    searchProductsForAddStock,
    searchSuppliers,
    fetchPostedReturnsForSource,
})(UpdateStock);
